import React, { useEffect, useMemo } from "react";
import { format } from "date-fns";
import {
  TextController,
  DateController,
  ReactSelectController,
  Text,
  TimeRangeController,
  Icons,
  Button,
  Divider,
  WeekDaysPickerController,
} from "@pushpress/shared-components";
import { Control, UseFormTrigger, useWatch } from "react-hook-form";
import { ClassForm } from "../CalendarForm.utils";
import { calendarItemRecurrence } from "./../../Calendar.utils";
import ClassEndsModal from "./ClassEndsModal";
import useToggle from "../../../../hooks/state";
import StaffProfileSelect from "../../../../components/controllers/StaffProfileSelect";
import CalendarItemTypeSelect from "../../../../components/controllers/CalendarItemTypeSelect";
import { changeTimeZone } from "../../../../utils/dateUtils";

type ClassDetailsProps = {
  control: Control<ClassForm>;
  editMode?: boolean;
  trigger: UseFormTrigger<ClassForm>;
  initialCalendarItemTypeUuid?: string;
};

const ClassDetails = ({
  control,
  editMode,
  trigger,
  initialCalendarItemTypeUuid,
}: ClassDetailsProps) => {
  const [classEndsModal, classEndsModalActions] = useToggle(false);

  const [classEnds, startTime, endTime, recurrence] = useWatch({
    control,
    name: ["classEnds", "time.start", "time.end", "recurrence"],
  });

  const classEndsText = useMemo(() => {
    if (!classEnds) return "";

    if (classEnds.type === "never") {
      return "Repeats forever";
    }

    if (classEnds.type === "on") {
      const dateOnUtc = changeTimeZone(classEnds.date, "UTC");
      return `Class ends on ${format(dateOnUtc, "MM/dd/yyyy")}`;
    }

    if (classEnds.type === "after") {
      return `Class ends after ${classEnds.number} occurrences`;
    }
  }, [classEnds]);

  // trigger validation after change values. Avoid fields show outdated messages.
  useEffect(() => {
    if (startTime && endTime) {
      trigger(["time.start", "time.end"]);
    }
  }, [startTime, endTime, trigger]);

  return (
    <div className="p-4">
      <ClassEndsModal
        visible={classEndsModal}
        onClose={classEndsModalActions.off}
        control={control}
      />
      <TextController
        control={control}
        name="name"
        required
        label="Class name"
      />
      <CalendarItemTypeSelect
        control={control}
        name="type"
        required
        label="Type"
        menuPortalTarget={document.body}
        initialCalendarTypeUuid={initialCalendarItemTypeUuid}
      />
      <DateController
        control={control}
        name="date.start"
        required
        label="Start date"
        startIcon={Icons.Classes}
        disabled={editMode}
      />
      <TimeRangeController
        label="Time"
        required
        className="mb-0"
        startTimeProps={{
          control,
          name: "time.start",
          required: true,
          label: "Start time",
          valueType: "12-hour",
        }}
        endTimeProps={{
          control,
          name: "time.end",
          required: true,
          label: "End time",
          valueType: "12-hour",
        }}
      />
      {!editMode && (
        <>
          <Divider vPadding={8} marginRight={-28} />
          <Text
            variant="heading-5"
            color="wolf-800"
            className="mt-3"
            weight="600"
          >
            Schedule
          </Text>
          <Text variant="body-md" color="wolf-600" className="mb-4">
            Set when this class occurs
          </Text>
          <ReactSelectController
            control={control}
            name="recurrence"
            required
            label="Repeats"
            options={calendarItemRecurrence}
            menuPortalTarget={document.body}
          />
          {recurrence?.value !== 0 && (
            <>
              <WeekDaysPickerController
                control={control}
                name="weekDays"
                label="Week days"
                required
              />
              <Button
                data-cy="class-ends-button"
                buttonType="text"
                className="mt-4 mb-4"
                text={classEndsText}
                onClick={classEndsModalActions.on}
              />
            </>
          )}
        </>
      )}
      <Divider vPadding={8} marginRight={-28} />
      <Text variant="heading-5" color="wolf-800" className="mt-3" weight="600">
        Coaches
      </Text>
      <Text variant="body-md" color="wolf-600">
        Assign coaches for this class
      </Text>
      <StaffProfileSelect
        className="mt-4"
        control={control}
        name="mainCoach"
        label="Coach"
        placeholder="— Coach —"
        menuPortalTarget={document.body}
        hideDropdownIndicator
        isClearable
        hideLabel
      />
      <StaffProfileSelect
        control={control}
        name="assistantCoach"
        label="Assistant Coach"
        placeholder="— Assistant coach —"
        menuPortalTarget={document.body}
        hideDropdownIndicator
        isClearable
        hideLabel
      />
    </div>
  );
};

export default ClassDetails;
